import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ActivityLogEntry,
  ActivityType,
  AdminStats,
  AdminUserSummary,
  Paginated,
} from '@app/common';
import { getStats, listActivity, listUsers } from '../api/admin.api';
import { SessionExpiredError } from '../api';

const PAGE_SIZE = 25;

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

/**
 * Kept separate from `useDashboardData` on purpose: these hooks are only mounted
 * by the admin tabs, so an ordinary user's dashboard never issues an admin
 * request and never sees a 403 it would have to explain away.
 */
function useAsync<T>(
  load: () => Promise<T>,
  /**
   * Everything the request depends on, flattened into one string. A plain array
   * would have to be spread into the dependency list, and React requires that
   * list to keep a constant size between renders.
   */
  key: string
): AsyncState<T> {
  const [state, setState] = useState<AsyncState<T>>({ data: null, loading: true, error: null });

  // `load` is a fresh closure every render, so it is deliberately not a
  // dependency — `key` is what decides when to refetch.
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));

    loadRef
      .current()
      .then((data) => {
        if (!cancelled) setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // The session-expired dialog already covers the screen in this case, so
        // an inline error underneath it would just be noise.
        if (err instanceof SessionExpiredError) {
          setState((prev) => ({ ...prev, loading: false }));
          return;
        }
        const message = err instanceof Error ? err.message : 'Request failed';
        setState({ data: null, loading: false, error: message });
      });

    return () => {
      cancelled = true;
    };
  }, [key]);

  return state;
}

/**
 * Adds a `reload` to the usual async state: banning a user changes a row this
 * list is already showing, and the table has to reflect it without a page
 * refresh. Bumping a nonce into the key is enough — `useAsync` refetches on any
 * key change, so there is no second code path to keep in step.
 */
export function useAdminUsers(page: number, search: string) {
  const [nonce, setNonce] = useState(0);

  const state = useAsync<Paginated<AdminUserSummary>>(
    () => listUsers({ page, pageSize: PAGE_SIZE, search: search || undefined }),
    `users:${page}:${search}:${nonce}`
  );

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { ...state, reload };
}

export function useAdminActivity(page: number, filters: { userId?: string; type?: ActivityType }) {
  return useAsync<Paginated<ActivityLogEntry>>(
    () =>
      listActivity({
        page,
        pageSize: PAGE_SIZE,
        userId: filters.userId,
        type: filters.type,
      }),
    `activity:${page}:${filters.userId ?? ''}:${filters.type ?? ''}`
  );
}

export function useAdminStats(days: number) {
  return useAsync<AdminStats>(() => getStats(days), `stats:${days}`);
}

export { PAGE_SIZE as ADMIN_PAGE_SIZE };
